import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const LiveChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [currentAgent, setCurrentAgent] = useState(null);

  const agents = [
    {
      id: 1,
      name: 'Sarah Chen',
      role: 'Digital Strategy Lead',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      expertise: ['Web Development', 'SEO Strategy'],
      status: 'online'
    },
    {
      id: 2,
      name: 'Marcus Rodriguez',
      role: 'App Development Expert',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      expertise: ['Mobile Apps', 'UI/UX Design'],
      status: 'online'
    },
    {
      id: 3,
      name: 'Emily Watson',
      role: 'Social Media Specialist',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      expertise: ['Social Media', 'Content Strategy'],
      status: 'away'
    }
  ];

  const quickQuestions = [
    "What services do you offer?",
    "How much does a website cost?",
    "Do you work with small businesses?",
    "What\'s your typical timeline?",
    "Can you help with SEO?"
  ];

  useEffect(() => {
    // Set current agent to first online agent
    const onlineAgent = agents?.find(agent => agent?.status === 'online');
    setCurrentAgent(onlineAgent);

    // Check business hours (9 AM - 6 PM IST)
    const now = new Date();
    const hour = now?.getHours();
    setIsOnline(hour >= 9 && hour < 18);

    // Initial welcome message
    if (onlineAgent) {
      setMessages([{
        id: 1,
        sender: 'agent',
        agent: onlineAgent,
        message: `Hi! I'm ${onlineAgent?.name}, your ${onlineAgent?.role}. How can I help you today?`,
        timestamp: new Date()
      }]);
    }
  }, []);

  const handleSendMessage = (message = newMessage) => {
    if (!message?.trim()) return;

    const userMessage = {
      id: messages?.length + 1,
      sender: 'user',
      message: message?.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');

    // Simulate agent response
    setTimeout(() => {
      const responses = [
        "Thanks for your question! Let me connect you with the right specialist for detailed information.",
        "Great question! I\'d be happy to discuss this further. Would you like to schedule a quick call?",
        "That's definitely something we can help with. Let me get you some specific information.",
        "Perfect! This is exactly the kind of project we love working on. Let\'s dive deeper into your needs."
      ];

      const agentResponse = {
        id: messages?.length + 2,
        sender: 'agent',
        agent: currentAgent,
        message: responses?.[Math.floor(Math.random() * responses?.length)],
        timestamp: new Date()
      };

      setMessages(prev => [...prev, agentResponse]);
    }, 1000 + Math.random() * 2000);
  };

  const handleQuickQuestion = (question) => {
    handleSendMessage(question);
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <>
      {/* Chat Widget Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <Button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-16 h-16 rounded-full shadow-lg brand-hover-lift ${
            isOpen 
              ? 'bg-muted-foreground hover:bg-muted-foreground/90' 
              : 'bg-accent hover:bg-accent/90'
          }`}
          size="icon"
        >
          {isOpen ? (
            <Icon name="X" size={24} className="text-white" />
          ) : (
            <div className="relative">
              <Icon name="MessageCircle" size={24} className="text-white" />
              {isOnline && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-white animate-pulse"></div>
              )}
            </div>
          )}
        </Button>
      </div>
      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 h-[500px] bg-background border border-border rounded-2xl shadow-xl z-40 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-accent text-white p-4 flex items-center space-x-3">
            {currentAgent && (
              <>
                <div className="relative">
                  <Image
                    src={currentAgent?.avatar}
                    alt={currentAgent?.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                    currentAgent?.status === 'online' ? 'bg-success' : 'bg-warning'
                  }`}></div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold">{currentAgent?.name}</h4>
                  <p className="text-sm text-white/80">{currentAgent?.role}</p>
                </div>
                <div className="flex items-center space-x-1">
                  <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-success' : 'bg-warning'}`}></div>
                  <span className="text-xs">{isOnline ? 'Online' : 'Away'}</span>
                </div>
              </>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages?.map((message) => (
              <div
                key={message?.id}
                className={`flex ${message?.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${
                  message?.sender === 'user' ?'bg-accent text-white' :'bg-surface text-foreground'
                } rounded-2xl px-4 py-2`}>
                  {message?.sender === 'agent' && message?.agent && (
                    <div className="flex items-center space-x-2 mb-1">
                      <Image
                        src={message?.agent?.avatar}
                        alt={message?.agent?.name}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                      <span className="text-xs font-medium">{message?.agent?.name}</span>
                    </div>
                  )}
                  <p className="text-sm">{message?.message}</p>
                  <p className={`text-xs mt-1 ${
                    message?.sender === 'user' ? 'text-white/70' : 'text-muted-foreground'
                  }`}>
                    {formatTime(message?.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Questions */}
          {messages?.length <= 1 && (
            <div className="p-4 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3">Quick questions:</p>
              <div className="space-y-2">
                {quickQuestions?.slice(0, 3)?.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickQuestion(question)}
                    className="w-full text-left text-sm p-2 rounded-lg bg-surface hover:bg-muted brand-transition"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-border">
            <div className="flex space-x-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && handleSendMessage()}
                placeholder={isOnline ? "Type your message..." : "Leave a message..."}
                className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                disabled={!isOnline}
              />
              <Button
                onClick={() => handleSendMessage()}
                disabled={!newMessage?.trim() || !isOnline}
                size="icon"
                className="bg-accent hover:bg-accent/90"
              >
                <Icon name="Send" size={16} className="text-white" />
              </Button>
            </div>
            {!isOnline && (
              <p className="text-xs text-muted-foreground mt-2">
                We're currently offline. Leave a message and we'll get back to you!
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LiveChatWidget;